import {
  IField,
  ISelect,
  ISelectOption,
} from 'app/services/api5-service/api.interface';
import { AutocompleteSelectOption } from 'components/autocomplete-select/model/autocomplete-select-option.interface';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';

import { FieldSettings } from 'common/dynamic-form/wrappers/field-base/model';

import { AutocompleteSelectProps } from './model/autocomplete-select-props.interface';

import { ISPFieldConfig, ISPFieldType, ISPFieldWrapper } from '../../model';
import { ISPFormState } from '../../model/form-state.interface';
import { getDropdownParentSelector } from '../select/dropdown.utils';

/**
 * Get autocomplete select options
 *
 * @param options
 */
export function getAutocompleteOptions(
  options: ISelectOption[] | null,
): AutocompleteSelectOption[] | null {
  return (
    options?.map(o => ({
      k: o.$key,
      v: o.$,
    })) || null
  );
}

/**
 * Get props for autocomplete select field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getAutocompleteProps(
  control: ISelect,
  field: IField,
  state: ISPFormState,
): AutocompleteSelectProps {
  // @WARN
  // Do not use the dependent fields mechanism as it works for the select component,
  // because the options are generated on the BE and they are already filtered
  // Therefore, you do not need to pay attention to the depend flag for the autocomplete field
  const options$ = state.selectService
    .getOptionsForSelect$(control.$name)
    .pipe(map(options => options || null));

  return {
    originalControl: control,
    originalField: field,
    fieldSettings$: of({
      displayMode: 'input-like',
      tagPlace: 'field',
    } as FieldSettings),
    // @TODO i.ablov make context with stream
    emptyMsg$: of(state.context.notFoundMsg),
    optionsUi$: options$.pipe(map(options => getAutocompleteOptions(options))),
    parentScrollSelector: getDropdownParentSelector(state, control, field),
  };
}

/**
 * Get config for autocomplete select field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getAutocompleteConfig(
  control: ISelect,
  field: IField,
  state: ISPFormState,
): ISPFieldConfig<ISPFieldType.AutocompleteSelect> {
  const props = getAutocompleteProps(control, field, state);

  return {
    key: control.$name,
    type: ISPFieldType.AutocompleteSelect,
    wrappers: [ISPFieldWrapper.FieldBase],
    props,
    modelOptions: {
      updateOn: 'change',
    },
  };
}
